import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, { type InventoryReportItem } from "@/services/report";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type StockReportFilters = {
  warehouse_id: number | null;
  per_page: number | "all";
};

const createInitialFilters = (): StockReportFilters => ({
  warehouse_id: null,
  per_page: 10,
});

export function useStockReport() {
  const { t, locale } = useI18n();
  const items = ref<InventoryReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const selectedProductIds = ref<number[]>([]);
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<StockReportFilters>(createInitialFilters());
  const i18nScope = "stock_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page === "all" ? total.value || 10 : Number(filters.per_page),
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100", "all"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const summary = computed(() => ({
    totalQty: items.value.reduce((sum, item) => sum + Number(item.instock_qty ?? 0), 0),
    totalValue: items.value.reduce((sum, item) => {
      if (Number(item.type) !== 1) return sum;
      return sum + Number(item.instock_qty ?? 0) * Number(item.cost ?? 0);
    }, 0),
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value,
    warehouse_id: filters.warehouse_id || undefined,
  }));

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Failed to fetch warehouses for stock report:", error);
    }
  };

  const fetchStockReport = async () => {
    loading.value = true;
    try {
      const response = await reportService.getInventoryReports(queryParams.value);
      items.value = response.data ?? [];
      currentPage.value = response.current_page;
      total.value = response.total;
      selectedProductIds.value = [];
    } catch (error) {
      console.error("Failed to fetch stock report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number | string) => {
    currentPage.value = Number(page);

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize === "all" ? "all" : Number(pageSize);
      currentPage.value = 1;
    }

    fetchStockReport();
  };

  const updateSelectedProducts = (ids: number[]) => {
    selectedProductIds.value = ids;
  };

  const exportStockReport = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportInventoryReports({
        warehouse_id: filters.warehouse_id ? String(filters.warehouse_id) : undefined,
        search_query: searchQuery.value,
        selected_products: selectedProductIds.value.length ? selectedProductIds.value : undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      let fileName = `inventory_report_${new Date().getTime()}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) fileName = match[1];
      }
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export stock report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchStockReport();
  });

  watch(
    () => filters.warehouse_id,
    () => {
      currentPage.value = 1;
      fetchStockReport();
    }
  );

  onMounted(async () => {
    await fetchWarehouses();
    await fetchStockReport();
  });

  const formatReportAmount = (value?: string | number) =>
    Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return {
    pageTitle,
    i18nScope,
    items,
    warehouses,
    loading,
    exporting,
    searchQuery,
    selectedProductIds,
    filters,
    pagination,
    summary,
    formatReportAmount,
    handleTableChange,
    updateSelectedProducts,
    exportStockReport,
  };
}
